import React, { useState } from 'react';
import { X } from 'lucide-react';
import { vendorApi } from '../services/api';
import { VendorCreate } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface VendorCreateModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function VendorCreateModal({ onClose, onSuccess }: VendorCreateModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [formData, setFormData] = useState<VendorCreate>({
    business_name: '',
    description: '',
    bio: '',
    website: '',
    social_media_facebook: '',
    social_media_instagram: '',
    product_types: [],
  });

  const [productTypeInput, setProductTypeInput] = useState('');

  const handleAddProductType = () => {
    const trimmed = productTypeInput.trim();
    if (trimmed && !formData.product_types?.includes(trimmed)) {
      setFormData({
        ...formData,
        product_types: [...(formData.product_types || []), trimmed]
      });
      setProductTypeInput('');
    }
  };

  const handleRemoveProductType = (type: string) => {
    setFormData({
      ...formData,
      product_types: formData.product_types?.filter(t => t !== type) || []
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate user email
    if (!userEmail.trim()) {
      setError('User email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password (optional - backend will require it only if user doesn't exist)
    if (userPassword && userPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Business name validation
    if (!formData.business_name.trim()) {
      setError('Business name is required');
      return;
    }
    if (formData.business_name.length > 255) {
      setError('Business name must be 255 characters or less');
      return;
    }

    // Description length validation
    if (formData.description && formData.description.length > 1000) {
      setError('Description must be 1000 characters or less');
      return;
    }

    // Bio length validation
    if (formData.bio && formData.bio.length > 2000) {
      setError('Bio must be 2000 characters or less');
      return;
    }

    // URL validation helper
    const isValidUrl = (url: string) => {
      if (!url) return true; // Empty is OK
      return url.startsWith('http://') || url.startsWith('https://');
    };

    // Validate URLs
    if (formData.website && !isValidUrl(formData.website)) {
      setError('Website URL must start with http:// or https://');
      return;
    }
    if (formData.social_media_facebook && !isValidUrl(formData.social_media_facebook)) {
      setError('Facebook URL must start with http:// or https://');
      return;
    }
    if (formData.social_media_instagram && !isValidUrl(formData.social_media_instagram)) {
      setError('Instagram URL must start with http:// or https://');
      return;
    }

    try {
      setSaving(true);
      await vendorApi.adminCreateVendor(userEmail, formData, userPassword || undefined);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to create vendor:', apiError);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">Create New Vendor</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

          {/* User Email */}
          <div>
            <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 mb-2">
              User Email *
            </label>
            <input
              id="user-email"
              data-testid="create-vendor-email"
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
              aria-label="User Email *"
            />
            <p className="text-xs text-gray-500 mt-1">
              Email of the user to create this vendor profile for
            </p>
          </div>

          {/* User Password */}
          <div>
            <label htmlFor="user-password" className="block text-sm font-medium text-gray-700 mb-2">
              Password (for new users)
            </label>
            <input
              id="user-password"
              data-testid="create-vendor-password"
              type="password"
              value={userPassword}
              onChange={(e) => setUserPassword(e.target.value)}
              placeholder="Enter password (min 8 characters)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              minLength={8}
              aria-label="Password (for new users)"
            />
            <p className="text-xs text-gray-500 mt-1">
              If the user doesn't exist, a new account will be created with this password. If the user exists, this field is ignored.
            </p>
          </div>

          {/* Business Name */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="business-name" className="block text-sm font-medium text-gray-700">
                Business Name *
              </label>
              <span className={`text-xs ${(formData.business_name?.length || 0) > 255 ? 'text-red-600' : 'text-gray-500'}`}>
                {formData.business_name?.length || 0}/255
              </span>
            </div>
            <input
              id="business-name"
              data-testid="create-vendor-business-name"
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              placeholder="Enter business name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
              maxLength={255}
              aria-label="Business Name *"
            />
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <span className={`text-xs ${(formData.description?.length || 0) > 1000 ? 'text-red-600' : 'text-gray-500'}`}>
                {formData.description?.length || 0}/1000
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the business"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              maxLength={1000}
            />
          </div>

          {/* Bio */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Bio
              </label>
              <span className={`text-xs ${(formData.bio?.length || 0) > 2000 ? 'text-red-600' : 'text-gray-500'}`}>
                {formData.bio?.length || 0}/2000
              </span>
            </div>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Artist/vendor biography"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              maxLength={2000}
            />
          </div>

          {/* Product Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Types
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={productTypeInput}
                onChange={(e) => setProductTypeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProductType())}
                placeholder="Add product type"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleAddProductType}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Add
              </button>
            </div>
            {formData.product_types && formData.product_types.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.product_types.map((type, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {type}
                    <button
                      type="button"
                      onClick={() => handleRemoveProductType(type)}
                      className="text-green-600 hover:text-green-800"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Social Media */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram
              </label>
              <input
                type="url"
                value={formData.social_media_instagram}
                onChange={(e) => setFormData({ ...formData, social_media_instagram: e.target.value })}
                placeholder="https://instagram.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facebook
              </label>
              <input
                type="url"
                value={formData.social_media_facebook}
                onChange={(e) => setFormData({ ...formData, social_media_facebook: e.target.value })}
                placeholder="https://facebook.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving || !userEmail.trim() || !formData.business_name.trim()}
            >
              {saving ? 'Creating...' : 'Create Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
